import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  useToast
} from '@chakra-ui/react';
import { t } from 'i18next';
import { isEmpty, isUndefined } from 'lodash';
import { useState } from 'react';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import { useNavigate } from 'react-router-dom';
import ToastMessageBox from '../alerts/ToastMessageBox';
import createChatRoom from '../api/mutations/legacy/createChatRoom';
import useExchangeablePhotoCardsQuery from '../api/queries/useExchagablePhotoCardsQuery';
import PrimaryButton from '../buttons/PrimaryButton';
import SecondaryLineButton from '../buttons/SecondaryLineButton';
import CheckIcon from '../icons/CheckIcon';
import GridSkeletons from '../skeletons/GridSkeletons';
import TradingPhotoCardInfo from '../tradings/TradingPhotoCardInfo';

export default function ExchangeablePhotoCardBottomDrawer({
  isOpen,
  onClose,
  focusRef,
  tradingPhotoCard,
  onConfirmedPhotoCard
}) {
  const navigate = useNavigate();
  const toast = useToast();

  const exchangeablePhotoCards = useExchangeablePhotoCardsQuery({
    userId: tradingPhotoCard.userId,
    artistId: tradingPhotoCard.artistId,
    memberId: tradingPhotoCard.memberId
  });

  const [selectedPhotoCard, setSelectedPhotoCard] = useState(null);

  const hasErrorCode = () => {
    return !isEmpty(exchangeablePhotoCards.error);
  };

  const navigateChatRoom = async () => {
    const response = await createChatRoom(
      tradingPhotoCard.artistId,
      tradingPhotoCard.tradingPhotoCardId
    );
    if (response.success) {
      navigate(`/chats/${response.chatRoom.chatRoomId}?artistId=${tradingPhotoCard.artistId}`);
    } else {
      toast({
        duration: 1500,
        render: () => (
          <ToastMessageBox message={t('exploreTradingPhotoCardBottomSheet.errorNoProfileToast')} />
        )
      });
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" finalFocusRef={focusRef}>
      <DrawerOverlay />
      <DrawerContent
        motionProps={{
          variants: {
            enter: {
              y: '0%',
              transition: { duration: 0.15 }
            },
            exit: {
              y: '100%',
              transition: { duration: 0.1 }
            }
          }
        }}
        style={{
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px'
        }}
      >
        <DrawerHeader>
          <div className="font-bold text-[17px] leading-none mb-[20px] text-main-light-1 dark:text-main-dark-1">
            {t('tradingPhotoCardPage.matchedTradingPhotoCardTarget')}
          </div>
          <TradingPhotoCardInfo
            tradingPhotoCard={tradingPhotoCard}
            showProfile={true}
            showPrice={false}
          />
        </DrawerHeader>
        <DrawerBody p={0}>
          <div className="px-[24px]">
            <div className="font-bold text-[17px] leading-none mb-[5px] text-main-light-1 dark:text-main-dark-1">
              {t('ExchangeablePhotoCardBottomDrawer.subtitle')}
            </div>
            <div className="text-[13px] leading-none text-main-light-3 dark:text-main-dark-3">
              {t('ExchangeablePhotoCardBottomDrawer.description')}
            </div>
          </div>
          {isUndefined(exchangeablePhotoCards) ? (
            <GridSkeletons
              className={'mx-[24px] mt-[12px] mb-[114px]'}
              gridSize={4}
              skeletonSize={3}
              skeletonHeight={'90px'}
            />
          ) : isEmpty(exchangeablePhotoCards) ? (
            <RenderError errorCode={'NONE'} navigateChatRoom={navigateChatRoom} />
          ) : hasErrorCode() ? (
            <RenderError
              errorCode={exchangeablePhotoCards.error.code}
              navigateChatRoom={navigateChatRoom}
            />
          ) : (
            <>
              <div className="mt-[14px] mb-[20px] w-full">
                <ScrollMenu wrapperClassName="padding-left-scroll-container">
                  {exchangeablePhotoCards.map((photoCard) => (
                    <button
                      key={photoCard.photoCardId}
                      type="button"
                      className="mr-[4px] cursor-pointer relative"
                      onClick={() => setSelectedPhotoCard(photoCard)}
                    >
                      <div
                        className={`rounded-[12px] transition-all border ${
                          selectedPhotoCard?.photoCardId === photoCard.photoCardId
                            ? 'border-primary-light dark:border-primary-dark ring-1 ring-primary-light dark:ring-primary-dark'
                            : 'border-transparent'
                        }`}
                      >
                        <img
                          src={photoCard.mediumThumbnailUrl || photoCard.pictureUrl}
                          className="min-w-[57px] w-[57px] h-[90px] rounded-[8px] object-cover"
                          style={{ aspectRatio: '55/87' }}
                          loading="lazy"
                          alt=""
                        />
                      </div>
                      {selectedPhotoCard?.photoCardId === photoCard.photoCardId && (
                        <div className="rounded-tl-[8px] rounded-br-[8px] p-[3px] absolute right-0 bottom-0 bg-primary-light dark:bg-primary-dark">
                          <CheckIcon width={'16px'} height={'16px'} fill="white" />
                        </div>
                      )}
                    </button>
                  ))}
                </ScrollMenu>
              </div>

              <DrawerFooter
                className="flex flex-col safeAreaMarginBottom px-[24px!important]"
                width={'100%'}
                style={{ padding: 0 }}
              >
                <PrimaryButton
                  text={t('confirm')}
                  disabled={isEmpty(selectedPhotoCard)}
                  onClick={() => onConfirmedPhotoCard(selectedPhotoCard)}
                  width="100%"
                  style={{ padding: '16px 0px', fontSize: '17px' }}
                />
                <div className="text-[13px] leading-none mt-[14px] mb-4 text-main-light-3 dark:text-main-dark-3">
                  {t('ExchangeablePhotoCardBottomDrawer.disclaimer')}
                </div>
              </DrawerFooter>
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

const RenderError = ({ errorCode, navigateChatRoom }) => {
  const navigate = useNavigate();

  const errorTitle = () => {
    switch (errorCode) {
      case 'INVALID_USER_ID':
      case 'EMPTY_COLLECTED_PHOTO_CARDS':
        return '교환 가능한 포카가 없어요';
      default:
        return '보유중인 포카가 없어요';
    }
  };

  const navigateCollectPhotoCards = () => {
    navigate('/collections');
  };

  return (
    <div className="bg-surface-light dark:bg-surface-dark flex flex-col mx-[24px] p-[32px] text-center rounded-[8px] my-[16px]">
      <div className="font-medium text-[15px] text-main-light-3 dark:text-main-dark-3 mb-[20px]">
        {errorTitle()}
      </div>
      <PrimaryButton
        className="py-[11px!important]"
        text={'보유 포카 체크하러가기'}
        onClick={() => navigateCollectPhotoCards()}
        width="100%"
      />
      {errorCode !== 'INVALID_USER_ID' && (
        <SecondaryLineButton
          className={'py-[11px] text-[14px] mt-[6px]'}
          text={'그냥 상대방에게 찔러보기'}
          onClick={navigateChatRoom}
        />
      )}
    </div>
  );
};
